/* https://react.semantic-ui.com/modules/search/#types-standard */
import React from 'react';
import classNames from 'classnames';

import SearchIcon from '@geobank/assets/src/icons/SearchIcon';
import CrossIcon from '@geobank/assets/src/icons/CrossIcon';
import CustomScrollBarContainer from '../CustomScrollbarContainer/CustomScrollBarContainer';
import Preloader from './Preloader';

import styles from './SearchInput.css';

export interface SearchResultItem {
  id: string;
  title: string;
  description?: string;
  type?: string;
  geometry?: any;
  data?: any;
}

// { [key: string]: string }
interface SearchInputClasses {
  root?: string;
  input?: string;
}

interface SearchInputProps {
  // TODO - получать value из props, чтобы не терять значение при переключении view
  // value: string | SearchResultItem;
  classes?: SearchInputClasses; // className?: string;
  placeholder?: string;
  isLoading: boolean;
  clearOnSelect?: boolean;
  clearOnSubmit?: boolean;
  resultsHeightMax?: string;
  filterOptions?: (options: SearchResultItem[], state: SearchInputState) => SearchResultItem[];
  results?: SearchResultItem[];
  onSearchChange?: (value: string) => void; // onChange
  onResultSelect?: (value: SearchResultItem) => void;
  onSubmit?: (value: string) => void; // TODO - (value: string | SearchResultItem) => void;
  onClear?: () => void;
  icon?: React.ReactNode;
}

interface SearchInputState {
  value: string;
  isOpen: boolean;
}

export default class SearchInput extends React.Component<SearchInputProps, SearchInputState> {
  public state: SearchInputState = {
    value: '',
    isOpen: false,
  };

  public inputRef: React.RefObject<HTMLInputElement>;
  public containerRef: React.RefObject<HTMLDivElement>;

  constructor(props: any) {
    super(props);
    this.inputRef = React.createRef();
    this.containerRef = React.createRef();
  }

  public componentDidUpdate(prevProps: SearchInputProps) {
    if (prevProps.isLoading && !this.props.isLoading && this.props.results) {
      this.setState({ isOpen: true });
    }
  }

  // TODO - может быть создавать и удалять обработчики при открытии панели?
  public componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
  }
  public componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  private handleClickOutside = (event: any) => {
    if (!this.state.isOpen) return;
    if (this.containerRef.current && !this.containerRef.current.contains(event.target)) {
      this.handleClose();
    }
  };

  private handleChooseItem = (item: SearchResultItem) => {
    const { onResultSelect, clearOnSelect } = this.props;

    // при выборе закрыть результаты (isOpen: false or this.handleClose)
    if (clearOnSelect === true) {
      this.setState({ value: '', isOpen: false }); // this.handleClear()
    } else {
      this.setState({ value: item.title, isOpen: false });
    }
    onResultSelect && onResultSelect(item);
  };

  private readonly handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onSearchChange } = this.props;
    const value = event.target.value;
    const trimmedValue = value.trim();

    this.setState({ value });

    // this.handleSubmit();
    if (onSearchChange && trimmedValue.length > 0) {
      onSearchChange(trimmedValue);
    }
  };

  private readonly keyDownEventHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      this.handleClose();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.handleSubmit();
    }
  };

  // handleValueChange
  private handleSubmit = (_value?: string) => {
    const { onSubmit, clearOnSubmit } = this.props;
    const value = this.inputRef.current!.value.trim(); // or this.state.value

    if (onSubmit && value.length > 0) {
      onSubmit(value);
    }
    if (clearOnSubmit === true) {
      this.inputRef.current && this.inputRef.current.blur();
      this.setState({ value: '', isOpen: false }); // this.handleClear()
    }
  };

  public handleClose = () => {
    this.inputRef.current && this.inputRef.current.blur();
    this.setState({ isOpen: false });
  };

  public handleClear = () => {
    this.setState({ value: '', isOpen: false }, () => {
      this.props.onClear && this.props.onClear();
    });
  };

  private handleFocus = () => {
    this.setState({ isOpen: true });
  };

  // TODO - add search "button" ?
  public renderActions() {
    const { isLoading } = this.props;
    const { value } = this.state;
    let content;

    if (isLoading) {
      content = <Preloader fill="#14306a" />;
    } else if (value) {
      content = (
        <span onClick={this.handleClear} style={{ cursor: 'pointer' }}>
          <CrossIcon fill="#14306a" />
        </span>
      );
    } else {
      // TODO - инфо иконка с хелпом, когда пустая строка
    }
    return content;
  }

  private renderResults() {
    const { resultsHeightMax, results: _results, filterOptions } = this.props;
    let content = null;
    // let results: SearchResultItem[] = [];

    // в случае ошибки results = null/undefined
    if (!Array.isArray(_results)) {
      return content;
    }

    const results = filterOptions ? filterOptions(_results, this.state) : _results;

    if (results.length === 0) {
      content = (
        <div className={styles.results}>
          <div className={styles.notFoundText}>Результаты не найдены</div>
        </div>
      );
    } else {
      content = (
        <div className={styles.results}>
          <CustomScrollBarContainer
            width={'100%'}
            // height={'50vh'}
            heightMax={resultsHeightMax || '50vh'}
            color={'#9c9c9c'}
          >
            {results.map((item, index) => (
              <div
                key={index}
                className={styles.resultsItem}
                onClick={() => this.handleChooseItem(item)}
              >
                {/* TODO - иконка по типу элемента */}
                <div>
                  <span>{item.title}</span>
                </div>
                <div>{item.description || ''}</div>
              </div>
            ))}
          </CustomScrollBarContainer>
        </div>
      );
    }
    return content;
  }

  public render() {
    const { placeholder, icon } = this.props; // className -> classes.root
    const classes = this.props.classes || {}; // root (container), form, input, results
    const { value, isOpen } = this.state;

    return (
      <div ref={this.containerRef} className={classNames(styles.root, classes.root)}>
        <form className={styles.form}>
          <input
            ref={this.inputRef}
            className={classNames(styles.input, classes.input)}
            value={value}
            placeholder={placeholder || 'Введите строку'}
            type="text"
            onChange={this.handleValueChange}
            onKeyDown={this.keyDownEventHandler}
            // onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            // onMouseDown={this.handleMouseDown}
          />
          <div className={styles.actionsBlock}>{this.renderActions()}</div>
          <button type="button" className={styles.submitButton} onClick={() => this.handleSubmit()}>
            {icon ? icon : <SearchIcon />}
          </button>
        </form>
        {/* TODO - добавить оверлей с полупрозрачным фоном на всю высоту
          левого блока
        */}
        {isOpen && this.renderResults()}
      </div>
    );
  }
}
